import { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useNavigate } from 'react-router';
import { Bell, User, TrendingUp, Package, CheckCircle, Clock, AlertCircle, FileText, LifeBuoy, ChevronRight, ChevronDown, Receipt, RefreshCw, ArrowRight, Search, Truck, Wallet } from 'lucide-react';
import logo from '@/assets/599ad72d49c8887573a4750887df95ce60e36150.png';
import { DriverBottomNav } from '../components/DriverBottomNav';
import { DriverCoachmark } from '../components/DriverCoachmark';
import { DriverLocationPermissionCheck } from '../components/DriverLocationPermissionCheck';
import { useDriverOnboarding } from '../hooks/useDriverOnboarding';
import { HOME_PAGE_STEPS_INTERNAL, HOME_PAGE_STEPS_EXTERNAL } from '../utils/driverOnboardingSteps';
import { requestNotificationPermission, startLocationTracking, areNotificationsEnabled, getLastKnownLocation } from '../utils/notifications';
import { bidService } from '../services/bidService';
import { accountingService } from '../services/accountingService';
import { shipmentServiceReal } from '../services/shipmentServiceReal';
import { notificationService, NOTIFICATIONS_CHANGED_EVENT, isNotificationRead } from '../services/notificationService';
import { driverService } from '../services/driverService';
import { trailerService } from '../services/trailerService';
import { vehicleService } from '../services/vehicleService';
import { emptyReturnService } from '../services/emptyReturnService';
import { alwaysLocationService } from '../services/alwaysLocationService';
import { warehouseService } from '../services/warehouseService';
import { driverDefenseService } from '../services/driverDefenseService';
import { API_CONFIG, getAuthToken } from '../config/api';
import { getStoredDriverMobileUser, setStoredDriverMobileUser } from '../utils/driverMobileSession';
import { toast } from 'sonner';
import { PendingAssignmentDetailSheet } from '../components/PendingAssignmentDetailSheet';
import { isInternalDriverUser } from '../utils/userRoles';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { isCorporateExternalTransportMobileUser } from '../utils/corporateMobileUser';

interface OfferRequest {
  id: string;
  shipmentId: string;
  date: string;
  weight: string;
  origin: string;
  originCity: string;
  destination: string;
  destinationCity: string;
  currentBestOffer?: number;
  myOffer?: number;
  status: 'pending' | 'evaluated' | 'approved' | 'rejected';
  offerCount?: number;
  palletCount?: number;
}

interface PendingAssignment {
  id: string;
  shipmentNo: string;
  pickupName: string;
  pickupCity: string;
  pickupDistrict?: string;
  deliveryName: string;
  deliveryCity: string;
  deliveryDistrict?: string;
  assignedAt?: string;
  pickupDate?: string;
}

interface ActiveShipment {
  id: string;
  shipmentNo: string;
  pickupName: string;
  pickupCity: string;
  pickupDistrict?: string;
  deliveryName: string;
  deliveryCity: string;
  deliveryDistrict?: string;
  status: string;
  vehiclePlate?: string;
  trailerPlate?: string;
  assignedAt?: string;
  sortDate?: string;
}

interface ActiveWaitingRecord {
  id: string;
  shipmentId?: string;
  shipmentNo?: string;
  waitingStart?: string;
}

const toLocalMysqlDateTime = (date = new Date()) => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const mapProcessStatusLabel = (value?: string, options?: { assignmentAccepted?: boolean; internalDriver?: boolean }) => {
  switch (String(value || '').trim()) {
    case 'atandi-islem-bekliyor':
      if (options?.internalDriver && options?.assignmentAccepted) return 'Yükleme Tamamlandı';
      return 'Atandı İşlem Bekliyor';
    case 'yukleme-bekliyor':
      return 'Yükleme Bekliyor';
    case 'yukleme-devam':
      return 'Yükleme Devam';
    case 'yukleme-tamamlandi':
      return 'Yükleme Tamamlandı';
    case 'tasima-yolunda':
      return 'Teslim Yolunda';
    case 'bosaltma-bekliyor':
      return 'Boşaltma Bekliyor';
    case 'bosaltma-devam':
      return 'Boşaltma Başladı';
    case 'bosaltma-tamamlandi':
      return 'Boşaltma Tamamlandı';
    default:
      return value || '-';
  }
};

const isActiveDriverShipment = (item: any) => {
  const processStatus = String(item?.process_status || item?.processStatus || '').trim();
  const shipmentStatus = String(item?.status || '').trim().toLocaleLowerCase('tr-TR');
  const assignmentResponseStatus = String(item?.assignment_response_status || item?.assignmentResponseStatus || '').trim().toLocaleLowerCase('tr-TR');
  const currentStage = Number(item?.current_stage ?? item?.currentStage ?? 0);

  if (assignmentResponseStatus && assignmentResponseStatus !== 'accepted') return false;
  if (['delivered', 'completed', 'cancelled', 'canceled'].includes(shipmentStatus)) return false;
  if (['bosaltma-tamamlandi', 'iptal', 'tamamlandi'].includes(processStatus)) return false;
  if (Number.isFinite(currentStage) && currentStage >= 9) return false;
  return true;
};

export function DriverMobileHome() {
  const navigate = useNavigate();
  const { showTour, initialized, advanceTour, skipTour } = useDriverOnboarding('home');
  const [user, setUser] = useState<any>(null);
  const [offers, setOffers] = useState<OfferRequest[]>([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const notificationLoadId = useRef(0);
  const [pendingAssignments, setPendingAssignments] = useState<PendingAssignment[]>([]);
  const [activeShipments, setActiveShipments] = useState<ActiveShipment[]>([]);
  const [driverProfile, setDriverProfile] = useState<any>(null);
  const [availableTrailers, setAvailableTrailers] = useState<any[]>([]);
  const [selectedTrailerId, setSelectedTrailerId] = useState('');
  const [trailerSearch, setTrailerSearch] = useState('');
  const [showTrailerList, setShowTrailerList] = useState(false);
  const [changingTrailer, setChangingTrailer] = useState(false);
  const [togglingDriverStatus, setTogglingDriverStatus] = useState(false);
  const [highlightVehicleCard, setHighlightVehicleCard] = useState(false);
  const [showEmptyReturnWarning, setShowEmptyReturnWarning] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedPendingAssignmentId, setSelectedPendingAssignmentId] = useState<string | null>(null);
  const [selectedActiveShipmentId, setSelectedActiveShipmentId] = useState<string | null>(null);
  const autoOpenedActiveShipmentRef = useRef(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [activeOfferTab, setActiveOfferTab] = useState<'pending' | 'evaluated'>('pending');
  const [myBids, setMyBids] = useState<any[]>([]);
  const [driverStats, setDriverStats] = useState({
    completedJobs: 0,
    totalEarnings: 0,
  });
  const [externalAccountingStats, setExternalAccountingStats] = useState({
    invoiceWaiting: 0,
    paymentWaiting: 0,
  });
  const [offersWarning, setOffersWarning] = useState('');
  const [lastSharedLocationAt, setLastSharedLocationAt] = useState<string | null>(null);
  const [activeWaitingRecord, setActiveWaitingRecord] = useState<ActiveWaitingRecord | null>(null);
  const [pendingDefenseCount, setPendingDefenseCount] = useState(0);
  const [waitingActionLoading, setWaitingActionLoading] = useState(false);
  const isInternalDriver = isInternalDriverUser(user);
  const isCorporateMobileUser = isCorporateExternalTransportMobileUser(user);
  const normalizedVehicleAvailability = String(driverProfile?.vehicle_status || driverProfile?.status || 'active').trim().toLocaleLowerCase('tr-TR');
  const isVehicleMoving = Number(driverProfile?.vehicle_is_running || 0) === 1 || Number(driverProfile?.vehicle_current_speed || 0) > 0;

  useEffect(() => {
    console.log("HOME TOKEN:", localStorage.getItem("token"));
    const storedUser = getStoredDriverMobileUser<any>();
    if (!storedUser) {
      navigate('/driver-mobile-auth', { replace: true });
      return;
    }
    try {
      setUser(storedUser);
      setOffers([]);
      loadNotifications(storedUser.id);
      if (isCorporateExternalTransportMobileUser(storedUser)) {
        loadOffers(storedUser.id);
        loadMyBids(storedUser.id, storedUser);
        loadExternalDriverAccountingStats();
        loadDriverStats(storedUser.id, storedUser);
        return;
      }
      loadDriverStats(storedUser.id);
      if (isInternalDriverUser(storedUser)) {
        loadInternalDriverContext(storedUser.id);
        loadPendingAssignments(storedUser.id);
        loadPendingDefenseCount();
      } else {
        loadExternalDriverContext(storedUser.id);
        loadExternalDriverAccountingStats();
        loadOffers(storedUser.id);
        loadMyBids(storedUser.id);
      }
      
      // Initialize notification permissions if not already done
      if (!areNotificationsEnabled() && 'Notification' in window && Notification.permission === 'default') {
        // Don't auto-request, let user do it from settings
        console.log('Notifications not enabled. User can enable from settings.');
      }
      
      // On native iOS/webview do not probe browser geolocation automatically.
      if (!isInternalDriverUser(storedUser) && !isCorporateExternalTransportMobileUser(storedUser) && Capacitor.isNativePlatform()) {
        void (async () => {
          const authToken = getAuthToken();
          if (authToken) {
            await alwaysLocationService.configureTrackingIfAvailable({
              apiBaseUrl: API_CONFIG.BASE_URL,
              authToken,
              shipmentId: null,
            });
            await alwaysLocationService.startBackgroundTrackingIfAvailable();
          }
        })();
        void alwaysLocationService.requestAlwaysIfAvailable();
      }

      if (!isInternalDriverUser(storedUser) && !isCorporateExternalTransportMobileUser(storedUser) && !Capacitor.isNativePlatform() && 'geolocation' in navigator) {
        navigator.permissions?.query({ name: 'geolocation' }).then((result) => {
          if (result.state === 'granted') {
            startLocationTracking();
          }
        }).catch(() => {
          // Permission API not supported, try to get location anyway
          console.log('Permission API not supported');
        });
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      navigate('/driver-mobile-auth', { replace: true });
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!user?.id || isCorporateMobileUser) return;
    void loadActiveWaitingRecord();
  }, [user?.id, isCorporateMobileUser]);

  useEffect(() => {
    if (isInternalDriver) return;
    if (autoOpenedActiveShipmentRef.current || selectedActiveShipmentId || activeShipments.length === 0) return;
    autoOpenedActiveShipmentRef.current = true;
    setSelectedActiveShipmentId(activeShipments[0].id);
  }, [activeShipments, selectedActiveShipmentId, isInternalDriver]);

  const loadActiveWaitingRecord = async () => {
    try {
      const response: any = await warehouseService.getMyActiveWaitingRecord();
      const row = response?.data || null;
      setActiveWaitingRecord(row ? {
        id: String(row.id),
        shipmentId: row.shipment_id ? String(row.shipment_id) : '',
        shipmentNo: row.shipment_no || row.order_number || '',
        waitingStart: row.waiting_start || row.reported_at || '',
      } : null);
    } catch (error) {
      console.error('Active waiting record load failed:', error);
      setActiveWaitingRecord(null);
    }
  };

  const loadPendingDefenseCount = async () => {
    try {
      setPendingDefenseCount(await driverDefenseService.pendingCount());
    } catch (error) {
      console.error('Pending defense count load failed:', error);
      setPendingDefenseCount(0);
    }
  };

  useEffect(() => {
    if (isCorporateMobileUser) return;
    const syncLastLocation = () => {
      const location = getLastKnownLocation();
      setLastSharedLocationAt(location?.timestamp || null);
    };

    syncLastLocation();
    const handleUpdate = () => syncLastLocation();
    window.addEventListener('driverLocationUpdate', handleUpdate as EventListener);
    return () => window.removeEventListener('driverLocationUpdate', handleUpdate as EventListener);
  }, [isCorporateMobileUser]);

  useAutoRefresh(() => {
    if (!user?.id) return Promise.resolve();
    if (isCorporateExternalTransportMobileUser(user)) {
      return Promise.all([
        loadNotifications(String(user.id)),
        loadDriverStats(String(user.id), user),
        loadExternalDriverAccountingStats(),
        loadOffers(String(user.id)),
        loadMyBids(String(user.id), user),
      ]).then(() => undefined);
    }
    if (isInternalDriverUser(user)) {
      return Promise.all([
        loadNotifications(String(user.id)),
        loadDriverStats(String(user.id), user),
        loadInternalDriverContext(String(user.id)),
        loadPendingAssignments(String(user.id)),
        loadPendingDefenseCount(),
        loadActiveWaitingRecord(),
      ]).then(() => undefined);
    }

    return Promise.all([
      loadNotifications(String(user.id)),
      loadDriverStats(String(user.id)),
      loadExternalDriverContext(String(user.id)),
      loadExternalDriverAccountingStats(),
      loadOffers(String(user.id)),
      loadMyBids(String(user.id), user),
      loadActiveWaitingRecord(),
    ]).then(() => undefined);
  }, { intervalMs: 30000 });

  const loadOffers = async (driverId: string) => {
    try {
      const response: any = await bidService.getBidRequests({ status: 'open' });
      const data = Array.isArray(response?.data) ? response.data : [];
      setOffersWarning(String(response?.message || ''));
      if (data.length > 0) {
        const convertedOffers: OfferRequest[] = data
          .map((bid: any) => {
            let status: OfferRequest['status'] = 'pending';
            if (bid.status === 'awarded') status = 'approved';
            else if (bid.status === 'closed' || bid.status === 'completed') status = 'evaluated';
            else if (bid.status === 'rejected') status = 'rejected';
            
            return {
              id: String(bid.id),
              shipmentId: String(bid.shipment_no || bid.sevk_kodu || bid.shipment_id || bid.id),
              date: new Date(bid.bid_deadline || bid.planned_loading_time || bid.created_at || Date.now()).toLocaleDateString('tr-TR'),
              weight: String(bid.cargo_weight || 0),
              origin: bid.loading_point || bid.loading_address || 'Yükleme Noktası',
              originCity: bid.loading_city || '',
              destination: bid.delivery_point || bid.delivery_address || 'Teslimat Noktası',
              destinationCity: bid.delivery_city || '',
              currentBestOffer: bid.current_lowest_bid,
              offerCount: bid.bid_count || bid.total_bids || 0,
              palletCount: Number(bid.pallet_count || 0),
              status,
            };
          });
        setOffers(convertedOffers);
      } else {
        setOffers([]);
      }
    } catch (error) {
      console.error('Error loading offers:', error);
      toast.error('Teklifler yüklenirken hata oluştu');
    }
  };

  const loadMyBids = async (userId: string, sourceUser: any = user) => {
    try {
      if (isCorporateExternalTransportMobileUser(sourceUser)) {
        const companyId = String(sourceUser?.externalTransportAccountUserId || sourceUser?.external_transport_account_user_id || sourceUser?.id || '');
        const response: any = await bidService.getAll();
        const data = Array.isArray(response?.data) ? response.data : [];
        setMyBids(data.filter((item: any) => String(item.external_transport_account_user_id || '') === companyId));
        return;
      }
      const driverResponse: any = await driverService.getDriverByUserId(userId);
      const driver = driverResponse?.data || driverResponse || {};
      if (!driver?.id) return;
      const response: any = await bidService.getAll({ driverId: String(driver.id) });
      const data = Array.isArray(response?.data) ? response.data : [];
      setMyBids(data);
    } catch (error) {
      console.error('Error loading my bids:', error);
    }
  };

  const loadNotifications = async (userId: string) => {
    const loadId = ++notificationLoadId.current;
    try {
      const response: any = await notificationService.getNotifications({ userId, unreadOnly: true, compact: true, limit: 20 });
      if (loadId !== notificationLoadId.current) return;
      if (response.success && response.data) {
        const items = Array.isArray(response?.data) ? response.data : [];
        setUnreadNotifications(Number(response.unreadCount ?? items.filter((item: any) => !isNotificationRead(item)).length));
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    const refresh = () => { void loadNotifications(String(user.id)); };
    window.addEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);
    window.addEventListener('focus', refresh);
    return () => {
      window.removeEventListener(NOTIFICATIONS_CHANGED_EVENT, refresh);
      window.removeEventListener('focus', refresh);
      notificationLoadId.current += 1;
    };
  }, [user?.id]);

  const loadDriverStats = async (userId: string, sourceUser: any = user) => {
    try {
      if (isCorporateExternalTransportMobileUser(sourceUser)) {
        const completedJobs = myBids.filter((item: any) => ['accepted', 'awarded'].includes(String(item.status || '').trim())).length;
        setDriverStats({ completedJobs, totalEarnings: 0 });
        return;
      }
      const driverResponse: any = await driverService.getDriverByUserId(userId);
      const driver = driverResponse?.data || driverResponse || {};

      if (!driver?.id) return;

      setDriverStats({
        completedJobs: Number(driver.completed_shipments || driver.total_trips || 0),
        totalEarnings: Number(driver.total_earnings || 0),
      });
    } catch (error) {
      console.error('Error loading driver stats:', error);
    }
  };

  const loadExternalDriverAccountingStats = async () => {
    try {
      const response: any = await accountingService.getDriverShipments();
      const rows = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
      const invoiceWaiting = rows.filter((item: any) => ['invoice-waiting', 'invoice-rejected'].includes(String(item.accountingStatus || item.accounting_status || '').trim())).length;
      const paymentWaiting = rows.filter((item: any) => ['invoice-approval-pending', 'payment-pre-approval', 'payment-pending'].includes(String(item.accountingStatus || item.accounting_status || '').trim())).length;
      setExternalAccountingStats({ invoiceWaiting, paymentWaiting });
    } catch (error) {
      console.error('Error loading external driver accounting stats:', error);
      setExternalAccountingStats({ invoiceWaiting: 0, paymentWaiting: 0 });
    }
  };

  const loadEmptyReturnWarning = async () => {
    if (!isInternalDriver) {
      setShowEmptyReturnWarning(false);
      return;
    }
    try {
      const response: any = await emptyReturnService.getMyRecords();
      const records = Array.isArray(response?.data) ? response.data : [];
      const hasActiveEmptyReturn = records.some((item: any) => item.status === 'active');
      const hasActiveShipment = activeShipments.length > 0;
      const hasAssignedVehicle = Boolean(driverProfile?.assigned_vehicle_plate || driverProfile?.cekici_plaka);
      setShowEmptyReturnWarning(hasAssignedVehicle && isVehicleMoving && !hasActiveShipment && !hasActiveEmptyReturn);
    } catch (error) {
      console.error('Error loading empty return warning:', error);
      setShowEmptyReturnWarning(false);
    }
  };

  useEffect(() => {
    void loadEmptyReturnWarning();
  }, [isInternalDriver, isVehicleMoving, activeShipments.length, driverProfile?.assigned_vehicle_plate, driverProfile?.cekici_plaka]);

  const loadInternalDriverContext = async (userId: string) => {
    try {
      const [driverResponse, trailerResponse] = await Promise.all([
        driverService.getDriverByUserId(userId),
        trailerService.getTrailers(),
      ]);

      const driver = driverResponse?.data || driverResponse || {};
      const normalizedDriver = {
        ...driver,
        resolved_assigned_vehicle_id: driver.assigned_vehicle_id || driver.assignedVehicleId || null,
      };
      setDriverProfile(normalizedDriver);

      const shipmentsResponse: any = driver?.id
        ? await shipmentServiceReal.getShipments({ driverId: String(driver.id), assignmentResponseStatus: 'accepted' })
        : { data: [] };
      const shipmentRows = Array.isArray(shipmentsResponse?.data) ? shipmentsResponse.data : [];
      const activeRows = shipmentRows
        .filter(isActiveDriverShipment)
        .sort((a: any, b: any) => new Date(b.assigned_at || b.updated_at || b.created_at || 0).getTime() - new Date(a.assigned_at || a.updated_at || a.created_at || 0).getTime());
      setActiveShipments(activeRows.map((item: any) => ({
        id: String(item.id),
        shipmentNo: item.shipment_no || String(item.id),
        pickupName: item.pickup_name || 'Yükleme',
        pickupCity: item.pickup_city || '',
        pickupDistrict: item.pickup_district || '',
        deliveryName: item.delivery_name || 'Teslimat',
        deliveryCity: item.delivery_city || '',
        deliveryDistrict: item.delivery_district || '',
        status: mapProcessStatusLabel(item.process_status || item.status || '-', { internalDriver: true, assignmentAccepted: String(item.assignment_response_status || '').trim() === 'accepted' }),
        vehiclePlate: item.assigned_vehicle_plate || item.cekici_plaka || normalizedDriver.assigned_vehicle_plate || '-',
        trailerPlate: item.assigned_trailer_plate || item.dorse_plaka || normalizedDriver.assigned_trailer_plate || '-',
        assignedAt: item.assigned_at || '',
        sortDate: item.assigned_at || item.updated_at || item.created_at || '',
      })));

      const trailerRows = Array.isArray(trailerResponse?.data) ? trailerResponse.data : [];
      const eligibleTrailers = trailerRows.filter((item: any) => {
        const normalizedStatus = String(item.status || '').trim().toLocaleLowerCase('tr-TR');
        return !normalizedStatus || normalizedStatus === 'active' || normalizedStatus === 'aktif';
      }).map((item: any) => {
        return {
          ...item,
          currentVehiclePlate: item.linked_vehicle_plate || '',
          currentDriverName: '',
        };
      });
      setAvailableTrailers(eligibleTrailers);
      const matchedTrailer = eligibleTrailers.find((item: any) => String(item.plate || '') === String(normalizedDriver.assigned_trailer_plate || normalizedDriver.dorse_plaka || ''));
      setSelectedTrailerId(matchedTrailer ? String(matchedTrailer.id) : '');
      setTrailerSearch(matchedTrailer?.plate || normalizedDriver.assigned_trailer_plate || normalizedDriver.dorse_plaka || '');

      if (userId && user) {
        setStoredDriverMobileUser({
          ...user,
          tractorPlate: normalizedDriver.assigned_vehicle_plate || normalizedDriver.cekici_plaka || user.tractorPlate || null,
          trailerPlate: matchedTrailer?.plate || normalizedDriver.assigned_trailer_plate || normalizedDriver.dorse_plaka || null,
          assignedVehicleId: normalizedDriver.resolved_assigned_vehicle_id || user.assignedVehicleId || null,
        });
      }
    } catch (error) {
      console.error('Error loading internal driver context:', error);
    }
  };

  const loadExternalDriverContext = async (userId: string) => {
    try {
      const driverResponse: any = await driverService.getDriverByUserId(userId);
      const driver = driverResponse?.data || driverResponse || {};
      if (!driver?.id) {
        setActiveShipments([]);
        return;
      }

      setDriverProfile(driver);
      const shipmentsResponse: any = await shipmentServiceReal.getShipments({ driverId: String(driver.id), assignmentResponseStatus: 'accepted' });
      const shipmentRows = Array.isArray(shipmentsResponse?.data) ? shipmentsResponse.data : [];
      const activeRows = shipmentRows
        .filter(isActiveDriverShipment)
        .sort((a: any, b: any) => new Date(b.assigned_at || b.updated_at || b.created_at || 0).getTime() - new Date(a.assigned_at || a.updated_at || a.created_at || 0).getTime());

      setActiveShipments(activeRows.map((item: any) => ({
        id: String(item.id),
        shipmentNo: item.shipment_no || String(item.id),
        pickupName: item.pickup_name || 'Yükleme',
        pickupCity: item.pickup_city || '',
        pickupDistrict: item.pickup_district || '',
        deliveryName: item.delivery_name || 'Teslimat',
        deliveryCity: item.delivery_city || '',
        deliveryDistrict: item.delivery_district || '',
        status: mapProcessStatusLabel(item.process_status || item.status || '-', { internalDriver: false, assignmentAccepted: String(item.assignment_response_status || '').trim() === 'accepted' }),
        vehiclePlate: item.assigned_external_tractor_plate || item.assigned_vehicle_plate || item.cekici_plaka || driver.assigned_external_tractor_plate || driver.assigned_vehicle_plate || driver.cekici_plaka || '-',
        trailerPlate: item.assigned_external_trailer_plate || item.assigned_trailer_plate || item.dorse_plaka || driver.assigned_external_trailer_plate || driver.assigned_trailer_plate || driver.dorse_plaka || '-',
        assignedAt: item.assigned_at || '',
        sortDate: item.assigned_at || item.updated_at || item.created_at || '',
      })));
    } catch (error) {
      console.error('Error loading external driver context:', error);
      setActiveShipments([]);
    }
  };

  const filteredTrailers = availableTrailers.filter((item: any) => {
    const query = trailerSearch.trim().toLocaleLowerCase('tr-TR');
    if (!query) return true;
    return `${item.plate || ''} ${item.trailer_type || ''} ${item.brand || ''} ${item.model || ''} ${item.currentVehiclePlate || ''} ${item.currentDriverName || ''}`.toLocaleLowerCase('tr-TR').includes(query);
  });

  const canAcceptAssignments = Boolean(driverProfile?.assigned_trailer_plate || driverProfile?.dorse_plaka);

  const activeVehicleId = String(
    driverProfile?.assigned_vehicle_id ||
    driverProfile?.assignedVehicleId ||
    driverProfile?.resolved_assigned_vehicle_id ||
    user?.assignedVehicleId ||
    user?.vehicleId ||
    ''
  );

  const handleChangeActiveTrailer = async () => {
    if (!activeVehicleId || !selectedTrailerId) return;
    try {
      setChangingTrailer(true);
      await trailerService.updateTrailer(selectedTrailerId, { linked_vehicle_id: Number(activeVehicleId) });
      await loadInternalDriverContext(String(user.id));
      setShowTrailerList(false);
      setHighlightVehicleCard(true);
      window.setTimeout(() => setHighlightVehicleCard(false), 1600);
      toast.success('Aktif dorse güncellendi');
    } catch (error) {
      console.error('Active trailer update failed:', error);
      toast.error('Aktif dorse güncellenemedi');
    } finally {
      setChangingTrailer(false);
    }
  };

  const handleToggleDriverAvailability = async () => {
    const activeVehicleId = driverProfile?.assigned_vehicle_id || driverProfile?.assignedVehicleId || driverProfile?.resolved_assigned_vehicle_id || user?.assignedVehicleId || null;
    if (!activeVehicleId) {
      toast.error('Atanmış çekici bulunamadı');
      return;
    }
    const normalizedStatus = String(driverProfile?.vehicle_status || driverProfile?.status || 'active').trim().toLocaleLowerCase('tr-TR');
    const nextStatus = normalizedStatus === 'inactive' ? 'active' : 'inactive';
    try {
      setTogglingDriverStatus(true);
      await vehicleService.updateVehicle(activeVehicleId, { status: nextStatus });
      setDriverProfile((current: any) => current ? { ...current, vehicle_status: nextStatus } : current);
      toast.success(nextStatus === 'active' ? 'Çekici aktif yapıldı' : 'Çekici pasif yapıldı');
    } catch (error) {
      console.error('Driver status update failed:', error);
      toast.error('Çekici durumu güncellenemedi');
    } finally {
      setTogglingDriverStatus(false);
    }
  };

  const loadPendingAssignments = async (userId: string) => {
    try {
      const driverResponse: any = await driverService.getDriverByUserId(userId);
      const driver = driverResponse?.data || driverResponse || {};
      if (!driver?.id) return;

      const response: any = await shipmentServiceReal.getPendingAssignments(driver.id);
      const data = Array.isArray(response?.data) ? response.data : [];
      setPendingAssignments(data.map((item: any) => ({
        id: String(item.id),
        shipmentNo: item.shipment_no || String(item.id),
        pickupName: item.pickup_name || 'Yükleme',
        pickupCity: item.pickup_city || '',
        pickupDistrict: item.pickup_district || '',
        deliveryName: item.delivery_name || 'Teslimat',
        deliveryCity: item.delivery_city || '',
        deliveryDistrict: item.delivery_district || '',
        assignedAt: item.assigned_at || item.updated_at || item.created_at || '',
        pickupDate: item.pickup_date || '',
      })));
    } catch (error) {
      console.error('Error loading pending assignments:', error);
    }
  };

  const handleRespondAssignment = async (assignmentId: string, action: 'accepted' | 'rejected') => {
    if (action === 'accepted' && !canAcceptAssignments) {
      toast.error('Görevi kabul etmeden önce aktif dorse seçmelisiniz');
      return;
    }
    try {
      await shipmentServiceReal.respondAssignment(assignmentId, { action });
      setPendingAssignments((current) => current.filter((item) => item.id !== assignmentId));
      if (user?.id) {
        await loadPendingAssignments(String(user.id));
        if (action === 'accepted') {
          if (isInternalDriverUser(user)) {
            await loadInternalDriverContext(String(user.id));
          } else {
            await loadExternalDriverContext(String(user.id));
          }
        }
      }
      toast.success(action === 'accepted' ? 'Görev onaylandı' : 'Görev reddedildi');
    } catch (error) {
      console.error('Assignment response failed:', error);
      toast.error('Görev yanıtı kaydedilemedi');
    }
  };

  const stats = {
    primary: isInternalDriver ? pendingAssignments.length : activeShipments.length,
    secondary: isInternalDriver ? activeShipments.length : externalAccountingStats.invoiceWaiting,
    tertiary: isInternalDriver ? driverStats.completedJobs : externalAccountingStats.paymentWaiting,
    completedJobs: driverStats.completedJobs,
    totalEarnings: driverStats.totalEarnings,
  };

  const currentWaitingShipmentId = activeWaitingRecord?.shipmentId || '';
  const selectedShipment = activeShipments.find((shipment) => shipment.id === selectedActiveShipmentId) || activeShipments[0] || null;
  const waitingTargetShipment = activeShipments.find((shipment) => shipment.id === currentWaitingShipmentId) || selectedShipment || (activeWaitingRecord ? {
    id: activeWaitingRecord.shipmentId || '',
    shipmentNo: activeWaitingRecord.shipmentNo || '-',
    pickupName: '-',
    pickupCity: '',
    deliveryName: activeWaitingRecord.shipmentNo || 'Aktif Bekleme',
    deliveryCity: '',
    status: 'Bekleme Aktif',
  } : null);
  const visibleOffers = offers.filter(o => o.status === 'pending');
  const groupedOffers = visibleOffers.reduce((acc: Record<string, OfferRequest[]>, offer) => {
    const groupKey = `${offer.origin || 'Yükleme Noktası'}|${offer.originCity || ''}`;
    if (!acc[groupKey]) acc[groupKey] = [];
    acc[groupKey].push(offer);
    return acc;
  }, {});

  const toggleGroup = (groupKey: string) => {
    setCollapsedGroups((current) => ({
      ...current,
      [groupKey]: !current[groupKey],
    }));
  };

  useEffect(() => {
    setCollapsedGroups((current) => {
      let changed = false;
      const next = { ...current };
      Object.keys(groupedOffers).forEach((key) => {
        if (next[key] === undefined) {
          next[key] = false;
          changed = true;
        }
      });
      return changed ? next : current;
    });
  }, [groupedOffers]);

  const resolveRequiredLocation = async () => {
    if (isInternalDriver) {
      return {
        latitude: undefined,
        longitude: undefined,
        recordedAt: new Date().toISOString(),
        localRecordedAt: toLocalMysqlDateTime(),
        locationSource: 'seyir-mobile',
        locationDescription: selectedShipment?.vehiclePlate || driverProfile?.assigned_vehicle_plate || driverProfile?.cekici_plaka || undefined,
      };
    }

    if (Capacitor.isNativePlatform()) {
      const authToken = getAuthToken();
      if (authToken) {
        await alwaysLocationService.configureTrackingIfAvailable({
          apiBaseUrl: API_CONFIG.BASE_URL,
          authToken,
          shipmentId: waitingTargetShipment ? Number(waitingTargetShipment.id) : undefined,
        });
      }
      await alwaysLocationService.requestAlwaysIfAvailable();
      await alwaysLocationService.startBackgroundTrackingIfAvailable();
    }

    return await new Promise<{ latitude: number; longitude: number; recordedAt: string; localRecordedAt: string; locationSource: string; locationDescription?: string }>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Konum servisi desteklenmiyor'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          recordedAt: new Date().toISOString(),
          localRecordedAt: toLocalMysqlDateTime(),
          locationSource: 'driver-mobile',
          locationDescription: `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`,
        }),
        () => reject(new Error('Bekleme islemi icin konum izni zorunlu')),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    });
  };

  const handleStartWaiting = async () => {
    if (!waitingTargetShipment) {
      toast.error('Aktif devam eden gorev bulunamadi');
      return;
    }
    try {
      setWaitingActionLoading(true);
      const locationPayload = await resolveRequiredLocation();
      await warehouseService.startMobileWaiting({
        shipmentId: Number(waitingTargetShipment.id),
        ...locationPayload,
      });
      await loadActiveWaitingRecord();
      toast.success(`Bekleme baslatildi: ${waitingTargetShipment.shipmentNo}`);
    } catch (error: any) {
      console.error('Start waiting failed:', error);
      toast.error(error?.message || 'Bekleme baslatilamadi');
    } finally {
      setWaitingActionLoading(false);
    }
  };

  const handleStopWaiting = async () => {
    if (!activeWaitingRecord?.id) {
      toast.error('Aktif bekleme kaydi bulunamadi');
      return;
    }
    try {
      setWaitingActionLoading(true);
      const locationPayload = await resolveRequiredLocation();
      await warehouseService.stopMobileWaiting(activeWaitingRecord.id, locationPayload);
      await loadActiveWaitingRecord();
      toast.success(`Bekleme bitirildi: ${activeWaitingRecord.shipmentNo || ''}`.trim());
    } catch (error: any) {
      console.error('Stop waiting failed:', error);
      toast.error(error?.message || 'Bekleme bitirilemedi');
    } finally {
      setWaitingActionLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="safe-area-page min-h-dvh overflow-x-hidden bg-gray-50 dark:bg-[#0b0b0b]">
      {/* Header */}
      <div className="overflow-hidden rounded-b-3xl border-b border-slate-200 bg-white p-4 pb-6 shadow-sm dark:border-slate-800 dark:bg-gradient-to-r dark:from-blue-900 dark:to-blue-800 dark:text-white dark:shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 p-2 dark:bg-white">
              <img src={logo} alt="Logo" className="w-full h-full object-contain" />
            </div>
            <div>
              <p className="text-sm text-slate-500 dark:text-blue-200">Hoş geldiniz</p>
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">{user.name || user.fullName || user.full_name || user.username || '-'}</h1>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button data-tour="notifications"
              onClick={() => navigate('/driver-mobile-notifications')}
              className="relative rounded-xl bg-slate-100 p-2 text-slate-800 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
            >
              <Bell className="w-5 h-5" />
              {unreadNotifications > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {unreadNotifications}
                </span>
              )}
            </button>
            <button data-tour="profile-button"
              onClick={() => navigate('/driver-mobile-profile')}
              className="rounded-xl bg-slate-100 p-2 text-slate-800 ring-1 ring-slate-200 transition-colors hover:bg-slate-200 dark:bg-white/15 dark:text-white dark:ring-white/20 dark:hover:bg-white/20"
            >
              <User className="w-5 h-5" />
            </button>
          </div>
        </div>

        {isInternalDriver && (
          <div data-tour="driver-status" className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-5 shadow-sm dark:border-slate-800 dark:bg-black">
            <div className="mb-3 flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-white">Sürücü Durumu</p>
                <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">Boş veya dolu durumunuzu buradan anında değiştirin</p>
              </div>
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${normalizedVehicleAvailability === 'inactive' ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-200' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-200'}`}>
                {normalizedVehicleAvailability === 'inactive' ? 'PASIF' : 'AKTIF'}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => normalizedVehicleAvailability === 'inactive' ? handleToggleDriverAvailability() : undefined}
                disabled={togglingDriverStatus || normalizedVehicleAvailability !== 'inactive'}
                className={`min-h-[58px] rounded-2xl border-2 px-4 py-3 text-base font-bold transition ${normalizedVehicleAvailability !== 'inactive' ? 'border-emerald-500 bg-emerald-400 text-emerald-950 shadow-sm dark:border-emerald-400 dark:bg-emerald-500 dark:text-white' : 'border-emerald-200 bg-white text-emerald-700 dark:border-emerald-500/40 dark:bg-slate-900/60 dark:text-emerald-200'} disabled:opacity-60`}
              >
                {togglingDriverStatus && normalizedVehicleAvailability === 'inactive' ? 'Güncelleniyor...' : 'Boş'}
              </button>
              <button
                type="button"
                onClick={() => normalizedVehicleAvailability !== 'inactive' ? handleToggleDriverAvailability() : undefined}
                disabled={togglingDriverStatus || normalizedVehicleAvailability === 'inactive'}
                className={`min-h-[58px] rounded-2xl border-2 px-4 py-3 text-base font-bold transition ${normalizedVehicleAvailability === 'inactive' ? 'border-rose-500 bg-rose-400 text-rose-950 shadow-sm dark:border-rose-400 dark:bg-rose-500 dark:text-white' : 'border-rose-200 bg-white text-rose-700 dark:border-rose-500/40 dark:bg-slate-900/60 dark:text-rose-200'} disabled:opacity-60`}
              >
                {togglingDriverStatus && normalizedVehicleAvailability !== 'inactive' ? 'Güncelleniyor...' : 'Dolu'}
              </button>
            </div>
          </div>
        )}

        {waitingTargetShipment && !isCorporateMobileUser && (
          <section className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-[#161616]">
            <div className={`flex items-center justify-between gap-3 border-b px-4 py-3 ${activeWaitingRecord?.id ? 'border-emerald-100 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10' : 'border-amber-100 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10'}`}>
              <div className="flex min-w-0 items-center gap-3">
                <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${activeWaitingRecord?.id ? 'bg-emerald-600 text-white' : 'bg-amber-500 text-white'}`}>
                  <Clock className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-slate-900 dark:text-white">Bekleme kaydı</p>
                  <p className="truncate text-xs text-slate-600 dark:text-slate-300">#{waitingTargetShipment.shipmentNo} · {waitingTargetShipment.deliveryName}</p>
                </div>
              </div>
              <span className={`shrink-0 rounded-full px-2.5 py-1 text-[10px] font-bold ${activeWaitingRecord?.id ? 'bg-emerald-600 text-white' : 'bg-amber-200 text-amber-950 dark:bg-amber-400 dark:text-amber-950'}`}>
                {activeWaitingRecord?.id ? 'AKTIF' : 'HAZIR'}
              </span>
            </div>
            <div className="p-4">
              <p className="mb-3 text-xs text-slate-600 dark:text-slate-300">{activeWaitingRecord?.id ? `Başlangıç: ${activeWaitingRecord.waitingStart ? new Date(activeWaitingRecord.waitingStart).toLocaleString('tr-TR') : '-'}` : 'Teslim noktasındaki beklemeyi kayıt altına alın.'}</p>
              <button
                type="button"
                onClick={() => activeWaitingRecord?.id ? void handleStopWaiting() : void handleStartWaiting()}
                disabled={waitingActionLoading}
                className={`flex min-h-[54px] w-full items-center justify-center rounded-xl px-4 text-base font-bold text-white transition ${activeWaitingRecord?.id ? 'bg-emerald-600 hover:bg-emerald-700 dark:bg-emerald-500 dark:hover:bg-emerald-400' : 'bg-amber-500 hover:bg-amber-600 dark:bg-amber-500 dark:hover:bg-amber-400'} disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {waitingActionLoading ? 'İşleniyor...' : activeWaitingRecord?.id ? 'Beklemeyi Bitir' : 'Beklemeyi Başlat'}
              </button>
              <p className="mt-3 text-[11px] leading-4 text-slate-500 dark:text-slate-400">{isInternalDriver ? 'Özmal sürücülerde varsa Seyir/servis konumu kayda eklenir.' : 'Bu işlem için konum izni zorunludur.'}</p>
            </div>
          </section>
        )}

        {/* Stats Cards */}
        <div data-tour="stats-cards" className="mt-8 grid grid-cols-1 gap-3 min-[360px]:grid-cols-3">
          <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-2 mb-1">
              <Package className="w-4 h-4 text-blue-600 dark:text-blue-200" />
              <p className="text-xs leading-tight text-slate-600 dark:text-blue-200">{isInternalDriver ? 'Kabul Bekleyen' : 'Aktif Sevkiyat'}</p>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.primary}</p>
          </div>
          
          <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-300" />
              <p className="text-xs leading-tight text-slate-600 dark:text-blue-200">{isInternalDriver ? 'Aktif Sevkiyat' : 'Fatura Bekleyen'}</p>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.secondary}</p>
          </div>

          <div className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-3 dark:border-slate-800 dark:bg-slate-900">
            <div className="flex items-center gap-2 mb-1">
              <Truck className="w-4 h-4 text-emerald-600 dark:text-emerald-300" />
              <p className="text-xs leading-tight text-slate-600 dark:text-blue-200">{isInternalDriver ? 'Tamamlanan' : 'Ödeme Bekleyen'}</p>
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.tertiary.toLocaleString('tr-TR')}</p>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Konum Paylaşımı</p>
          <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-white">Konumunuz en son {lastSharedLocationAt ? new Date(lastSharedLocationAt).toLocaleString('tr-TR') : 'henüz paylaşılmadı'}</p>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Uygulamanın gönderdiği son canlı konum zamanı.</p>
        </div>
      </div>

      {!isInternalDriver && (
      <div data-tour="external-earnings" className="px-4 pt-6">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-slate-900 shadow-sm dark:border-none dark:bg-gradient-to-r dark:from-green-600 dark:to-green-700 dark:text-white dark:shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-sm text-emerald-700 dark:text-green-100">Bu Ay Kazanç</p>
              <p className="text-3xl font-bold">{stats.totalEarnings.toLocaleString('tr-TR')} ₺</p>
            </div>
            <TrendingUp className="w-12 h-12 text-emerald-600 dark:text-green-300" />
          </div>
          <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-green-100">
            <span>Tamamlanan: {stats.completedJobs} taşıma</span>
          </div>
        </div>
      </div>
      )}

      {isInternalDriver && (
        <div className="px-4 pt-6 space-y-4">
          <div className="grid grid-cols-1 gap-3 min-[380px]:grid-cols-2">
            <div className={`rounded-2xl bg-white border p-4 shadow-sm transition-all duration-500 ${highlightVehicleCard ? 'border-blue-300 ring-2 ring-blue-200 shadow-md shadow-blue-100' : 'border-slate-200'}`}>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Çekici</p>
              <p className="mt-2 text-lg font-bold text-slate-900">{driverProfile?.assigned_vehicle_plate || driverProfile?.cekici_plaka || '-'}</p>
              <p className="mt-1 text-xs text-slate-500">Atanan araç plakası</p>
            </div>
            <div className={`rounded-2xl bg-white border p-4 shadow-sm transition-all duration-500 ${highlightVehicleCard ? 'border-blue-300 ring-2 ring-blue-200 shadow-md shadow-blue-100' : 'border-slate-200'}`}>
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Aktif Dorse</p>
              <p className="mt-2 text-lg font-bold text-slate-900">{driverProfile?.assigned_trailer_plate || driverProfile?.dorse_plaka || '-'}</p>
              <p className="mt-1 text-xs text-slate-500">Bağlı treyler</p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Kalan Çalışma</p>
              <p className="mt-2 text-lg font-bold text-slate-900">{driverProfile?.available_drive_period || driverProfile?.remaining_drive_period || driverProfile?.remaining_work_time || '-'}</p>
              <p className="mt-1 text-xs text-slate-500">Takograf kalan süre</p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Günlük Çalışma</p>
              <p className="mt-2 text-lg font-bold text-slate-900">{driverProfile?.work_duration || '-'}</p>
              <p className="mt-1 text-xs text-slate-500">Takograf günlük süre</p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Bu Hafta</p>
              <p className="mt-2 text-lg font-bold text-slate-900">{driverProfile?.current_week_hours || '-'}</p>
              <p className="mt-1 text-xs text-slate-500">Haftalık sürüş süresi</p>
            </div>
            <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Son İki Hafta</p>
              <p className="mt-2 text-lg font-bold text-slate-900">{driverProfile?.two_week_hours || '-'}</p>
              <p className="mt-1 text-xs text-slate-500">İki haftalık toplam sürüş süresi</p>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Aktif Dorseyi Değiştir</p>
                <p className="text-xs text-slate-500 mt-1">Atanan çekici için farklı bir dorse seçebilirsiniz</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 min-[420px]:flex-row">
              <div className="relative flex-1 min-w-0">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={trailerSearch}
                    onChange={(e) => {
                      setTrailerSearch(e.target.value);
                      setSelectedTrailerId('');
                      setShowTrailerList(true);
                    }}
                    onFocus={() => setShowTrailerList(true)}
                    placeholder="Dorse ara veya seç"
                    className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-3 text-sm"
                  />
                </div>
                {showTrailerList && (
                  <div className="absolute z-20 mt-2 w-full max-h-72 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                    {filteredTrailers.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-slate-400">Uygun dorse bulunamadı</div>
                    ) : filteredTrailers.map((trailer: any) => (
                      <button
                        key={trailer.id}
                        type="button"
                        onClick={() => {
                          setSelectedTrailerId(String(trailer.id));
                          setTrailerSearch(trailer.plate || '');
                          setShowTrailerList(false);
                        }}
                        className="block w-full px-4 py-3 text-left hover:bg-gray-50"
                      >
                        <div className="font-medium text-gray-900">{trailer.plate || '-'}</div>
                        <div className="text-xs text-gray-500">{[trailer.trailer_type, trailer.brand, trailer.model].filter(Boolean).join(' / ') || 'Dorse'}</div>
                        {(trailer.currentVehiclePlate || trailer.currentDriverName) && (
                          <div className="mt-1 text-[11px] text-blue-600">{trailer.currentVehiclePlate || '-'}{trailer.currentDriverName ? ` / ${trailer.currentDriverName}` : ''}</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={handleChangeActiveTrailer} disabled={!selectedTrailerId || !activeVehicleId || changingTrailer} className="rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50 min-[420px]:self-start">
                {changingTrailer ? 'Güncelleniyor...' : 'Değiştir'}
              </button>
            </div>
            {!activeVehicleId && (
              <p className="mt-3 text-xs text-rose-600">Bu sürücü için atanmış çekici bilgisi bulunamadı.</p>
            )}
            {availableTrailers.length === 0 && (
              <p className="mt-3 text-xs text-amber-600">Bu çekici için uygun aktif dorse listesi bulunamadı.</p>
            )}
          </div>
        </div>
      )}

      <div data-tour="quick-links" className="px-4 pt-6">
        <button
          type="button"
          onClick={() => navigate('/driver-mobile-support')}
          className="w-full rounded-2xl bg-white border border-slate-200 shadow-sm p-4 text-left flex items-center justify-between hover:shadow-md transition-shadow"
        >
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center">
              <LifeBuoy className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm font-semibold text-slate-900">Yardim ve Destek Merkezi</p>
              <p className="text-xs text-slate-500 mt-1">Sorularinizi iletin, operasyon ekibinden yanit alin</p>
            </div>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400" />
        </button>
      </div>

      {isInternalDriver && (
        <div className="px-4 pt-4">
          <button
            onClick={() => navigate('/driver-mobile-earnings')}
            className="w-full rounded-2xl bg-white border border-slate-200 shadow-sm p-4 text-left flex items-center justify-between hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-cyan-100 text-cyan-700 flex items-center justify-center">
                <Receipt className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Masraf ve İzin İşlemleri</p>
                <p className="text-xs text-slate-500 mt-1">Mobil uygulamadan masraf ve izin talebi gönderin</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      )}

      {isInternalDriver && (
        <div className="px-4 pt-4">
          <button
            onClick={() => navigate('/driver-mobile-payrolls')}
            className="w-full rounded-2xl bg-white border border-slate-200 shadow-sm p-4 text-left flex items-center justify-between hover:shadow-md transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center">
                <Wallet className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">Maaş Bordroları</p>
                <p className="text-xs text-slate-500 mt-1">Bordrolarınızı görüntüleyin ve indirin</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      )}

      {isInternalDriver && (
        <div className="px-4 pt-4">
          <button onClick={() => navigate('/driver-mobile-defenses')} className="w-full rounded-2xl border border-rose-200 bg-rose-50 p-4 text-left shadow-sm transition-shadow hover:shadow-md">
            <div className="flex items-center justify-between"><div className="flex items-center gap-3"><div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-700"><AlertCircle className="h-5 w-5" /></div><div><div className="flex items-center gap-2"><p className="text-sm font-semibold text-slate-900">Savunma Talepleri</p>{pendingDefenseCount > 0 && <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-rose-600 px-1.5 py-0.5 text-xs font-bold text-white">{pendingDefenseCount}</span>}</div><p className="mt-1 text-xs text-slate-600">Ağır ihlaller için savunmanızı gönderin</p></div></div><ChevronRight className="h-5 w-5 text-slate-400" /></div>
          </button>
        </div>
      )}

      {isInternalDriver && showEmptyReturnWarning && (
        <div className="px-4 pt-4">
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-sm">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-900">Aracınız boş görünüyor ama hareket ediyorsunuz</p>
                <p className="text-xs text-amber-800 mt-1">Boş dönüş başlatılmamış. Gerekliyse Boş Dönüş ekranından süreci başlatın.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Open Offers */}
      <div className="px-4 pt-6">
        {isInternalDriver && pendingAssignments.length > 0 && (
          <div className="mb-6 space-y-3">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900">Kabul Bekleyen Görevler</h2>
            </div>
            {pendingAssignments.map((assignment) => (
              <div key={assignment.id} className="bg-white rounded-2xl shadow-sm border border-amber-200 p-4">
                <button type="button" onClick={() => setSelectedPendingAssignmentId(assignment.id)} className="mb-4 w-full text-left">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="text-sm text-amber-900 font-semibold">#{assignment.shipmentNo}</div>
                      <div className="text-xs text-gray-500 mt-1">Atama: {assignment.assignedAt ? new Date(assignment.assignedAt).toLocaleString('tr-TR') : '-'}</div>
                    </div>
                  <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-semibold">Onay Bekliyor</span>
                </div>
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 min-w-0">
                    <p className="text-xs font-medium text-emerald-700 mb-2">Yükleme</p>
                    <p className="text-sm font-bold text-gray-900 truncate">{assignment.pickupName}</p>
                    <p className="text-xs text-gray-500 mt-1 truncate">{[assignment.pickupCity, assignment.pickupDistrict].filter(Boolean).join(' / ')}</p>
                  </div>
                  <div className="rounded-2xl bg-rose-50 border border-rose-100 p-3 min-w-0">
                    <p className="text-xs font-medium text-rose-700 mb-2">Boşaltma</p>
                    <p className="text-sm font-bold text-gray-900 truncate">{assignment.deliveryName}</p>
                    <p className="text-xs text-gray-500 mt-1 truncate">{[assignment.deliveryCity, assignment.deliveryDistrict].filter(Boolean).join(' / ')}</p>
                  </div>
                </div>
                <div className="inline-flex items-center gap-2 text-sm font-medium text-amber-900">
                  Detayı Gör <ChevronRight className="h-4 w-4" />
                </div>
                </button>
                <div className={`grid gap-2 ${isInternalDriver ? 'grid-cols-1' : 'grid-cols-2'}`}>
                  <button onClick={() => handleRespondAssignment(assignment.id, 'accepted')} className="rounded-xl bg-green-600 text-white py-3 font-bold hover:bg-green-700">Onayla</button>
                  {!isInternalDriver && <button onClick={() => handleRespondAssignment(assignment.id, 'rejected')} className="rounded-xl bg-red-600 text-white py-3 font-bold hover:bg-red-700">Reddet</button>}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeShipments.length > 0 && (
          <div data-tour="active-shipments" className="mb-6 space-y-3">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-gray-900">Atanan Sevkiyatlarım</h2>
            </div>
            {activeShipments.map((shipment) => (
              <div key={shipment.id} className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-4 text-left">
                <button type="button" onClick={() => setSelectedActiveShipmentId(shipment.id)} className="w-full text-left">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-sm text-blue-700 font-semibold">#{shipment.shipmentNo}</div>
                    <div className="text-xs text-gray-500 mt-1">{shipment.vehiclePlate || '-'} / {shipment.trailerPlate || '-'}</div>
                    <div className="text-xs text-gray-500 mt-1">Atama: {shipment.assignedAt ? new Date(shipment.assignedAt).toLocaleString('tr-TR') : '-'}</div>
                  </div>
                  <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold">{shipment.status}</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 min-w-0">
                    <p className="text-xs font-medium text-emerald-700 mb-2">Yükleme</p>
                    <p className="text-sm font-bold text-gray-900 truncate">{shipment.pickupName}</p>
                    <p className="text-xs text-gray-500 mt-1 truncate">{[shipment.pickupCity, shipment.pickupDistrict].filter(Boolean).join(' / ')}</p>
                  </div>
                  <div className="rounded-2xl bg-rose-50 border border-rose-100 p-3 min-w-0">
                    <p className="text-xs font-medium text-rose-700 mb-2">Boşaltma</p>
                    <p className="text-sm font-bold text-gray-900 truncate">{shipment.deliveryName}</p>
                    <p className="text-xs text-gray-500 mt-1 truncate">{[shipment.deliveryCity, shipment.deliveryDistrict].filter(Boolean).join(' / ')}</p>
                  </div>
                </div>
                <div className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-slate-600">
                  Detayı Gör <ChevronRight className="w-4 h-4" />
                </div>
                </button>
                <button onClick={() => navigate(`/driver-mobile-shipment-tracking/${shipment.id}`)} className="mt-3 inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-3 text-sm font-bold text-white hover:bg-blue-700">
                  <Package className="w-4 h-4" /> Süreci Aç ve İlerle
                </button>
              </div>
            ))}
          </div>
        )}

        {!isInternalDriver && (
          <div data-tour="offers-section">
            <div className="flex items-center justify-between mb-3">
              <div className="flex gap-1 rounded-xl bg-gray-100 p-1">
                <button
                  onClick={() => setActiveOfferTab('pending')}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${activeOfferTab === 'pending' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                >
                  Teklif Bekleyen
                </button>
                <button
                  onClick={() => setActiveOfferTab('evaluated')}
                  className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${activeOfferTab === 'evaluated' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                >
                  Teklif Verilen
                </button>
              </div>
              <button onClick={() => { user && loadOffers(user.id); loadMyBids(user.id); }} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            {activeOfferTab === 'pending' && (
              <div className="space-y-4">
                {offersWarning ? <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{offersWarning}</div> : null}
                {Object.entries(groupedOffers).map(([groupKey, groupItems]) => {
              const [originName, originCity] = groupKey.split('|');
              const isCollapsed = Boolean(collapsedGroups[groupKey]);
              return (
                <div key={groupKey} className="space-y-3">
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupKey)}
                    className="w-full flex items-center justify-between rounded-2xl bg-blue-50 border border-blue-100 px-4 py-3 text-left"
                  >
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">Yükleme Noktası</p>
                      <p className="text-sm font-bold text-slate-900 mt-1">{originName}</p>
                      <p className="text-xs text-slate-500 mt-1">{originCity || '-'}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="rounded-xl bg-white px-3 py-2 text-center border border-blue-100">
                        <div className="text-lg font-bold text-blue-700">{groupItems.length}</div>
                        <div className="text-[10px] text-slate-500 uppercase">Talep</div>
                      </div>
                      {isCollapsed ? <ChevronRight className="w-5 h-5 text-blue-700" /> : <ChevronDown className="w-5 h-5 text-blue-700" />}
                    </div>
                  </button>

                  {!isCollapsed && groupItems.map((offer) => (
                    <button
                      key={offer.id}
                      onClick={() => navigate(`/driver-mobile-offer/${offer.id}`)}
                      className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-4 hover:shadow-md transition-shadow text-left"
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="bg-blue-100 text-blue-900 font-bold px-3 py-1 rounded-lg text-sm whitespace-nowrap">
                            {offer.shipmentId}
                          </span>
                          <span className="text-sm text-gray-500 whitespace-nowrap">{offer.date}</span>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-medium whitespace-nowrap">{offer.weight} ton</span>
                          <span className="px-2.5 py-1 rounded-lg bg-slate-100 text-slate-700 text-xs font-medium whitespace-nowrap">{offer.palletCount || 0}P</span>
                          <ArrowRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>

                      {offer.currentBestOffer && (
                        <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-full px-3 py-1.5 mb-3">
                          <span className="w-2 h-2 bg-green-500 rounded-full" />
                          <p className="text-xs text-green-700 font-bold">
                            GÜNCEL TEKLİF: {offer.currentBestOffer.toLocaleString('tr-TR')} TL
                          </p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 min-w-0">
                          <p className="text-xs font-medium text-emerald-700 mb-2 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-emerald-500" />Yükleme</p>
                          <p className="text-sm font-bold text-gray-900 truncate">{offer.origin}</p>
                          <p className="text-xs text-gray-500 mt-1 truncate">{offer.originCity}</p>
                        </div>
                        <div className="rounded-2xl bg-rose-50 border border-rose-100 p-3 min-w-0">
                          <p className="text-xs font-medium text-rose-700 mb-2 flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-rose-500" />Boşaltma</p>
                          <p className="text-sm font-bold text-gray-900 truncate">{offer.destination}</p>
                          <p className="text-xs text-gray-500 mt-1 truncate">{offer.destinationCity}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              );
            })}

                {visibleOffers.length === 0 && (
                  <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Şu an teklif bekleyen taşıma bulunmuyor</p>
                  </div>
                )}
              </div>
            )}

            {activeOfferTab === 'evaluated' && (
              <div className="space-y-3">
                {myBids.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
                    <Package className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">Henüz teklif vermediniz</p>
                  </div>
                ) : (
                  myBids.map((bid: any) => (
                    <div key={bid.id} className="w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-4 text-left">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="bg-purple-100 text-purple-900 font-bold px-3 py-1 rounded-lg text-sm whitespace-nowrap">
                            {bid.shipment_no || '#' + (bid.shipment_id || '')}
                          </span>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          bid.status === 'awarded' ? 'bg-green-100 text-green-700' :
                          bid.status === 'rejected' ? 'bg-red-100 text-red-700' :
                           bid.status === 'pending' ? 'bg-amber-100 text-amber-900' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {bid.status === 'awarded' ? 'Kazandı' :
                           bid.status === 'rejected' ? 'Reddedildi' :
                           bid.status === 'pending' ? 'Beklemede' : 'Değerlendirildi'}
                        </span>
                      </div>
                      {bid.bid_amount != null && (
                        <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-blue-50 border border-blue-200 px-3 py-1.5">
                          <span className="text-xs font-bold text-blue-700">
                            Teklif: {Number(bid.bid_amount).toLocaleString('tr-TR')} TL
                          </span>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 min-w-0">
                          <p className="text-xs font-medium text-emerald-700 mb-2">Yükleme</p>
                          <p className="text-sm font-bold text-gray-900 truncate">{bid.pickup_city || '-'}</p>
                        </div>
                        <div className="rounded-2xl bg-rose-50 border border-rose-100 p-3 min-w-0">
                          <p className="text-xs font-medium text-rose-700 mb-2">Boşaltma</p>
                          <p className="text-sm font-bold text-gray-900 truncate">{bid.delivery_city || '-'}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <PendingAssignmentDetailSheet shipmentId={selectedPendingAssignmentId} open={Boolean(selectedPendingAssignmentId)} onClose={() => setSelectedPendingAssignmentId(null)} title="Kabul Bekleyen Görev" />
      <PendingAssignmentDetailSheet shipmentId={selectedActiveShipmentId} open={Boolean(selectedActiveShipmentId)} onClose={() => setSelectedActiveShipmentId(null)} title="Atanan Sevkiyat" />

      {initialized && showTour && (
        <DriverCoachmark
          steps={isInternalDriver ? HOME_PAGE_STEPS_INTERNAL : HOME_PAGE_STEPS_EXTERNAL}
          onFinish={() => { advanceTour('assignments'); navigate('/driver-mobile-assignments?tour=1'); }}
          onSkip={skipTour}
        />
      )}

      {/* Bottom Navigation */}
      <DriverLocationPermissionCheck />
      <DriverBottomNav />
    </div>
  );
}
